import { Badge } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DetailField from 'src/components/DataDisplay/DetailField/DetailField'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import DotNumberLink from 'src/components/DataDisplay/DotNumberLink/DotNumberLink'
import {
  CARRIER_VERIFICATION_PLATFORM_SOURCE_LABEL,
  CarrierVerificationPlatformSource,
} from 'src/graphql/types/carriers'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import { voeProviderLabel } from 'src/lib/voeProvider.utils'

import type { CarrierDetailRecord } from '../carrier.types'
import { formatCarrierAddress } from '../carrier.utils'

import CarrierCurationControl from './CarrierCurationControl'
import CarrierEditForm from './CarrierEditForm'
import CarrierSummary from './CarrierSummary'
import VerificationRoutingEditForm from './VerificationRoutingEditForm'

/** The sections that can be swapped for a form. Only one is open at a time. */
export type CarrierEditableSection = 'details' | 'routing'

export interface CarrierDetailProps {
  carrier: CarrierDetailRecord
  /** Omitted when the caller has no way to edit. */
  onEdit?: () => void
  onEditRouting?: () => void
  /** Which section is showing its form, if any. */
  editingSection?: CarrierEditableSection | null
  onCancelEdit?: () => void
  onSaved?: () => void
}

/** Falls back to the raw value for a source the label map does not know. */
const sourceLabel = (source: string | undefined) =>
  source
    ? CARRIER_VERIFICATION_PLATFORM_SOURCE_LABEL[
        source as CarrierVerificationPlatformSource
      ] ?? source
    : null

/**
 * One carrier's record. Takes the carrier rather than fetching it, so it serves
 * a query result, a story and a test alike.
 *
 * Editing swaps only the section being edited: the carrier's identity above and
 * the sections below stay on screen, so correcting a field does not empty the
 * page around it.
 */
const CarrierDetail = ({
  carrier,
  onEdit,
  onEditRouting,
  editingSection = null,
  onCancelEdit,
  onSaved,
}: CarrierDetailProps) => (
  <div className="flex flex-col gap-5">
    {/* The curation control writes, so it only appears where a save can be
        observed — the same condition the edit button uses. */}
    <CarrierSummary
      carrier={carrier}
      actions={
        onSaved && (
          <CarrierCurationControl carrier={carrier} onSaved={onSaved} />
        )
      }
    />

    {editingSection === 'details' && onCancelEdit && onSaved ? (
      <CarrierEditForm
        carrier={carrier}
        onClose={onCancelEdit}
        onSaved={onSaved}
      />
    ) : (
      <DetailSection
        title="Carrier Details"
        description="Who this carrier is and how a verification reaches them."
        columns={3}
        actions={
          onEdit && (
            <DNButton variant="outline" onClick={onEdit}>
              Edit
            </DNButton>
          )
        }
      >
        <DetailField
          label="DOT number"
          // Passing the element unconditionally would always be truthy, so a
          // carrier with no DOT number would render a blank row instead of
          // saying so.
          value={
            carrier.dotNumber ? (
              <DotNumberLink dotNumber={carrier.dotNumber} />
            ) : null
          }
        />
        <DetailField label="DBA name" value={carrier.dbaName} />
        <DetailField label="Legal name" value={carrier.legalName} />

        {/* formatInternationalPhoneNumber returns undefined for anything that is
            not a clean US or MX number — an extension, or an international line.
            Falling back to the stored value keeps a real number visible rather
            than reporting it as missing on the screen that exists to answer
            whether we can reach this carrier at all. */}
        <DetailField
          label="Phone"
          value={
            formatInternationalPhoneNumber(carrier.phoneNumber) ??
            carrier.phoneNumber
          }
        />
        <DetailField
          label="Fax"
          value={
            formatInternationalPhoneNumber(carrier.faxNumber) ??
            carrier.faxNumber
          }
        />
        <DetailField label="Email" value={carrier.email} mono />

        <DetailField label="Website" value={carrier.website} />
        <DetailField
          label="Physical address"
          value={formatCarrierAddress(carrier)}
          span={2}
        />
      </DetailSection>
    )}

    {editingSection === 'routing' && onCancelEdit && onSaved ? (
      <VerificationRoutingEditForm
        carrier={carrier}
        onClose={onCancelEdit}
        onSaved={onSaved}
      />
    ) : (
      <DetailSection
        title="Verification Routing"
        description="Where this carrier takes verifications. A platform can be recorded by an admin here, heard by the voice agent on a call, or imported. Listing platforms is information for recruiters; only Platform only stops the automated email sequence. Anything a recruiter starts by hand still goes out."
        columns={3}
        actions={
          onEditRouting && (
            <DNButton variant="outline" onClick={onEditRouting}>
              Edit
            </DNButton>
          )
        }
      >
        <DetailField
          label="Contact policy"
          value={
            carrier.platformOnly
              ? 'Platform only — no automated outreach'
              : 'Direct outreach allowed'
          }
        />
        <DetailField
          label="Platforms"
          emptyLabel="No platforms recorded"
          span={2}
          // The list is passed only when there is one, so an empty set reports
          // itself through emptyLabel instead of rendering an empty <ul>.
          value={
            carrier.verificationPlatforms.length > 0 && (
              <ul className="flex flex-col gap-3">
                {carrier.verificationPlatforms.map((platform, index) => (
                  <li
                    key={platform.carrierVerificationPlatformId ?? index}
                    className="flex flex-col gap-0.5"
                  >
                    <div className="flex flex-wrap items-center gap-2 font-medium">
                      {voeProviderLabel(platform.provider)}
                      {platform.preferred && (
                        <Badge size="xs" variant="light">
                          Preferred
                        </Badge>
                      )}
                    </div>
                    {platform.notes?.trim() ? (
                      <div>{platform.notes}</div>
                    ) : (
                      <div className="italic text-doubleNickel-gray-400">
                        No notes
                      </div>
                    )}
                    {sourceLabel(platform.source) && (
                      <div className="text-xs text-doubleNickel-gray-500">
                        {sourceLabel(platform.source)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )
          }
        />
      </DetailSection>
    )}
  </div>
)

export default CarrierDetail
